import wellStatusColors from 'enums/well_status_colors.json';
import wellStatusGroups from 'enums/well_status_groups.json';
import wellStatusTypes from 'enums/well_status_types.json';

/**
 * The stored well-status vocabulary, exactly as the database holds it. Every
 * member is snake_case except `LTD`, which is stored in caps. Slot rows, filter
 * facets and the parquet all carry these values verbatim — nothing upcases,
 * downcases or titleizes on the way out.
 */
export type WellStatusValue = keyof typeof wellStatusTypes;

/**
 * The statuses only an operator sets, on a well authored through Create New Well: the
 * `create_new_well` group of the shared well_status_groups.json, which
 * WellStatusCalculator::NON_DERIVED_STATUSES reads too.
 *
 * Nothing ingested ever computes one, so until such a well is promoted no row carries any of
 * them. A surface that enumerates the whole vocabulary to describe REAL DATA — the Development
 * Area's "Wells" tiles, which force a zero for every status it knows — therefore shows four
 * permanent zeroes. Those surfaces skip these until something actually carries one.
 *
 * This is not a reason to drop them from a FILTER, though: slot rows copy `wells.status`
 * verbatim (`packages/drafts/well_slot_source.rb`), so the moment an authored well is promoted
 * into a package these are real values somebody needs to filter on.
 */
export const NON_DERIVED_WELL_STATUSES: ReadonlySet<string> = new Set(
  wellStatusGroups.create_new_well
);

/**
 * Whether a stored status is one an operator sets rather than one derivation computes.
 *
 * @param {string} value - The stored status (`pre_permit`, `producing`, …).
 * @return {boolean} True for the non-derived four.
 */
export const isNonDerivedWellStatus = (value: string): boolean =>
  NON_DERIVED_WELL_STATUSES.has(value);

/**
 * The display label for a stored well status, for the view layer only. The
 * underlying row value must stay the stored one so sorting, filtering and the
 * request payloads keep matching the backend.
 *
 * An unrecognised value passes through unchanged rather than being blanked —
 * the backend vocabulary can outgrow this map, and a status the user cannot see
 * is worse than one shown raw.
 *
 * @param {string | null | undefined} value - The stored status (`shut_in`, `LTD`, …).
 * @return {string} The display label, or the value itself when unmapped. Empty
 *   string for a null/undefined/empty input.
 */
export function wellStatusLabel(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return wellStatusTypes[value as WellStatusValue] ?? value;
}

/**
 * The ONE place well-status colours live (INS-13300): `enums/well_status_colors.json`, which
 * the backend reads too (WellStatus::COLORS), so design can re-tune a status in one file. The
 * four Create New Well statuses are Carbon's purple ramp, each shown on its own.
 */
export const WELL_STATUS_COLOR_MAP: Readonly<Record<string, string>> =
  wellStatusColors;
